/**
 * A tiny dependency-free trend sparkline rendered as an inline SVG polyline — deliberately not a
 * charting library, per the SPA's "no heavy runtime deps" constraint. It plots a numeric series
 * (e.g. per-result latency over time) scaled to fit the box. Fewer than two points cannot form a
 * line, so it renders nothing. It is labelled for assistive tech via `role="img"` + `aria-label`.
 */

export interface SparklineProps {
  values: readonly number[];
  label: string;
  width?: number;
  height?: number;
}

export function Sparkline({ values, label, width = 240, height = 40 }: SparklineProps): JSX.Element | null {
  if (values.length < 2) return null;

  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1; // avoid divide-by-zero for a flat series
  const stepX = width / (values.length - 1);

  const points = values
    .map((value, index) => {
      const x = index * stepX;
      // Invert Y so larger values sit higher; inset by 1px so the stroke isn't clipped.
      const y = height - 1 - ((value - min) / span) * (height - 2);
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');

  return (
    <svg
      className="sparkline"
      role="img"
      aria-label={label}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
    >
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
